/**
 * Dashboard — Sources Spine cutover (Phase 1 PR 12).
 *
 * The dashboard's job is now exactly three things: show the funnel
 * (where is my mail right now?), surface the mode-conditional next
 * step (Rollout: what's stuck / Operate: what's bleeding), and link to
 * outstanding action items. Everything else from the legacy dashboard
 * is gone — replaced by the focused triage view at /sources and the
 * upcoming enforcement blast-radius view at /enforcement (PR 13).
 *
 * Reads exclusively from GET /api/dashboard/summary (spine-driven).
 * Subscribes to the global TimeWindowSelector (PR 9) so the funnel +
 * at-risk counts re-fetch when the window changes.
 *
 * Mode-conditional sections render based on the org's domain modes,
 * not the user's choice — a multi-domain org with mixed modes sees
 * both whatsStuck (Rollout) and currentlyBleeding (Operate). A
 * single-mode org sees only the relevant card.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  AlertTriangle,
  ChevronRight,
  HelpCircle,
  Loader2,
  ShieldAlert,
  Wind,
} from "lucide-react";
import {
  api,
  type DashboardSummaryResponse,
  type DashboardFunnelSegment,
  type DashboardSourceRow,
} from "@/lib/api";
import { GettingStartedChecklist } from "@/components/dashboard/GettingStartedChecklist";
import { useAuthStore } from "@/stores/auth";
import { timeWindowToDays, useTimeWindowStore } from "@/lib/time-window";
import { useFeatureDiscovery } from "@/lib/ux-analytics";

export function Dashboard() {
  useFeatureDiscovery("dashboard");
  const { organization, activeClientOrg } = useAuthStore();
  const window = useTimeWindowStore((s) => s.window);
  const windowDays = timeWindowToDays(window);

  const [data, setData] = useState<DashboardSummaryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPaidTier =
    !!organization?.billing_tier && organization.billing_tier !== "free";

  // Dashboard summary drives the funnel + mode-conditional sections.
  // The redundant Action Items preview card was removed; the bell
  // dropdown and the /alerts page are the canonical surfaces for that
  // queue, so duplicating it here only made the dashboard noisier
  // without giving the user a different angle on the data.
  function fetchAll(signal: { cancelled: boolean }) {
    setLoading(true);
    setError(null);
    api
      .getDashboardSummary({ windowDays })
      .then((summary) => {
        if (signal.cancelled) return;
        setData(summary);
      })
      .catch((err: unknown) => {
        if (signal.cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (!signal.cancelled) setLoading(false);
      });
  }

  useEffect(() => {
    const signal = { cancelled: false };
    fetchAll(signal);

    // MSP client switches trigger a refetch via the global event.
    const handleClientChange = () => fetchAll(signal);
    globalThis.window?.addEventListener("msp-client-changed", handleClientChange);
    return () => {
      signal.cancelled = true;
      globalThis.window?.removeEventListener(
        "msp-client-changed",
        handleClientChange
      );
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays, activeClientOrg?.id]);

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-slate-900">
            Dashboard
          </h1>
          <p className="text-sm text-slate-500">
            Where your mail's going, what to fix next, and what's bleeding.
          </p>
        </div>
        <Link
          to="/help#feature-dashboard"
          className="flex items-center gap-1.5 text-xs md:text-sm text-slate-400 hover:text-amber-600 transition-colors"
        >
          <HelpCircle className="w-4 h-4" />
          <span className="hidden sm:inline">Need help?</span>
        </Link>
      </header>

      <GettingStartedChecklist isPaidTier={isPaidTier} />

      {error && (
        <div className="flex items-center gap-3 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-900">
          <AlertTriangle className="w-5 h-5" />
          <div>
            <div className="font-medium">Couldn't load dashboard</div>
            <div className="text-rose-800">{error}</div>
          </div>
        </div>
      )}

      {loading && !data && (
        <div className="flex items-center justify-center py-16 text-slate-500">
          <Loader2 className="w-5 h-5 animate-spin mr-2" />
          Loading dashboard…
        </div>
      )}

      {data && (
        <>
          <FunnelCard
            totalMessages={data.funnel.totalMessages}
            segments={data.funnel.segments}
            domains={data.funnel.domains}
            windowDays={data.windowDays}
          />

          {data.whatsStuck.show && (
            <StuckCard
              sources={data.whatsStuck.sources}
              windowDays={data.windowDays}
              totalActionItems={data.actionItems.total}
            />
          )}

          {data.currentlyBleeding.show && (
            <BleedingCard
              sources={data.currentlyBleeding.sources}
              windowDays={data.windowDays}
            />
          )}
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// FunnelCard — single stacked bar at the top of the page
// ---------------------------------------------------------------------------

interface FunnelCardProps {
  totalMessages: number;
  segments: DashboardFunnelSegment[];
  domains: Array<{ id: string; name: string }>;
  windowDays: number;
}

/**
 * Segment color palette. Stable across rerenders so clicking the same
 * segment always navigates to the same /sources filter and the bar
 * doesn't reflow visually.
 */
const SEGMENT_COLORS: Record<DashboardFunnelSegment["key"], string> = {
  approved_aligned: "bg-emerald-500",
  approved_misconfigured: "bg-rose-500",
  approved_no_data: "bg-emerald-200",
  forwarder: "bg-sky-400",
  unclassified: "bg-amber-400",
  not_mine: "bg-slate-300",
};

const SEGMENT_DOT_COLORS: Record<DashboardFunnelSegment["key"], string> = {
  approved_aligned: "bg-emerald-500",
  approved_misconfigured: "bg-rose-500",
  approved_no_data: "bg-emerald-200",
  forwarder: "bg-sky-400",
  unclassified: "bg-amber-400",
  not_mine: "bg-slate-300",
};

function FunnelCard({ totalMessages, segments, domains, windowDays }: FunnelCardProps) {
  const headerName = headerDomainLabel(domains);
  // Empty state: no messages observed in the window. Bar would be empty
  // and unclickable; render a softer empty-state instead.
  if (totalMessages === 0) {
    return (
      <section className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="text-sm text-slate-500">
          No messages observed for {headerName} in the last {windowDays} day
          {windowDays === 1 ? "" : "s"} yet. Aggregates will appear here once
          DMARC reports arrive.
        </div>
      </section>
    );
  }

  const visible = segments.filter((s) => s.count > 0);

  // PR 33 (H4). Across-bar DMARC pass rate. The bar's segments are
  // source-state buckets; this single number tells the user the
  // overall authentication outcome that the segment colors don't
  // directly encode. Belt-and-suspenders: tooltip + legend also show
  // per-segment pass/fail, but this lede is the answer to "is my
  // mail authenticating?" at one glance.
  const totalPass = visible.reduce((sum, s) => sum + s.dmarcPassCount, 0);
  const totalAuthed = visible.reduce(
    (sum, s) => sum + s.dmarcPassCount + s.dmarcFailCount,
    0
  );
  const overallPassPct =
    totalAuthed > 0 ? Math.round((totalPass / totalAuthed) * 100) : null;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
      <div className="text-sm text-slate-700">
        <span className="font-semibold text-slate-900">
          {totalMessages.toLocaleString()}
        </span>{" "}
        messages seen as <span className="font-medium">{headerName}</span> in
        the last {windowDays} day{windowDays === 1 ? "" : "s"}
        {overallPassPct !== null && (
          <>
            {" · "}
            <span
              className="font-medium text-emerald-700"
              title="Share of messages in this window that passed DMARC (any segment, any source state)."
            >
              {overallPassPct}% pass DMARC
            </span>
          </>
        )}
      </div>

      {/* Stacked bar. Each segment is a click target into /sources filtered.
          PR 33 (H4) note: segment colors reflect the *source* state
          (Approved+Aligned, Unclassified, etc.), not the per-message
          DMARC outcome. The tooltip + legend below carry the
          per-segment pass/fail breakdown so the source-state vs
          message-outcome distinction stays honest. */}
      <div
        className="flex h-3 w-full rounded-full overflow-hidden bg-slate-100"
        role="img"
        aria-label={`Funnel: ${visible
          .map((s) => `${Math.round(s.share * 100)}% ${s.label}`)
          .join(", ")}`}
      >
        {visible.map((s) => (
          <Link
            key={s.key}
            to={segmentLinkFor(s.key)}
            className={`${SEGMENT_COLORS[s.key]} hover:opacity-80 transition-opacity`}
            style={{ width: `${s.share * 100}%` }}
            title={tooltipForSegment(s)}
          />
        ))}
      </div>

      {/* Legend with click targets — secondary pass/fail line per segment. */}
      <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5 text-xs text-slate-600">
        {visible.map((s) => (
          <li key={s.key}>
            <Link
              to={segmentLinkFor(s.key)}
              className="flex flex-col gap-0.5 hover:text-slate-900"
            >
              <span className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-sm ${SEGMENT_DOT_COLORS[s.key]}`} />
                <span className="font-medium">{Math.round(s.share * 100)}%</span>
                <span>{s.label}</span>
                <span className="text-slate-400">
                  ({s.count.toLocaleString()})
                </span>
              </span>
              {/* PR 33 (H4). Per-segment DMARC outcome breakdown so the
                  user knows what fraction of each source-state bucket
                  actually authenticates. Suppressed when the segment
                  has no DMARC verdicts in the window.
                  PR 64 (#3) — emphasise the fail count when it dominates.
                  The Unclassified bucket where 3,314 messages failed and
                  only 267 passed read as a quiet tile because the
                  failing count was rendered in the same muted text as
                  passing. Generic threshold (`fails > passes AND fails
                  > 0`) — no per-segment hardcoding. */}
              {(s.dmarcPassCount > 0 || s.dmarcFailCount > 0) && (
                <span className="pl-5 text-[11px] text-slate-500">
                  {s.dmarcPassCount.toLocaleString()} pass ·{" "}
                  <span
                    className={
                      s.dmarcFailCount > s.dmarcPassCount && s.dmarcFailCount > 0
                        ? "font-semibold text-rose-700"
                        : ""
                    }
                  >
                    {s.dmarcFailCount.toLocaleString()} fail
                  </span>
                </span>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * Build the title tooltip for one funnel segment. Includes both the
 * source-state framing (what the segment counts) and the per-message
 * DMARC outcome breakdown (PR 33, H4 fix). The point is to make the
 * conflation between source state and message outcome explicit on
 * hover, not to hide it.
 */
function tooltipForSegment(s: DashboardFunnelSegment): string {
  const pct = Math.round(s.share * 100);
  const head = `${s.label}: ${s.count.toLocaleString()} msgs (${pct}% of window)`;
  const hasOutcomes = s.dmarcPassCount > 0 || s.dmarcFailCount > 0;
  if (!hasOutcomes) return head;
  return (
    `${head}\n` +
    `Of those: ${s.dmarcPassCount.toLocaleString()} passed DMARC, ` +
    `${s.dmarcFailCount.toLocaleString()} failed.`
  );
}

function headerDomainLabel(domains: Array<{ id: string; name: string }>): string {
  if (domains.length === 0) return "your domains";
  if (domains.length === 1) return domains[0]!.name;
  if (domains.length <= 3) return domains.map((d) => d.name).join(", ");
  return `${domains.slice(0, 2).map((d) => d.name).join(", ")} +${domains.length - 2}`;
}

/**
 * Map a funnel segment to a /sources URL fragment. PR 11 ships
 * client-side bucket grouping; the segment->bucket map below mirrors
 * what /sources renders so clicking the bar lands on the same set of
 * Sources the funnel counted.
 *
 * The /sources page doesn't currently consume a `?classification=` or
 * `?health=` query param; for now every segment links to /sources and
 * the user picks the right section. PR 14+ may add real query-driven
 * filters; until then this URL is a placeholder that gets the user to
 * the right page.
 */
function segmentLinkFor(_key: DashboardFunnelSegment["key"]): string {
  return "/sources";
}

// ---------------------------------------------------------------------------
// StuckCard / BleedingCard — mode-conditional source list
// ---------------------------------------------------------------------------

interface SourceListCardProps {
  sources: DashboardSourceRow[];
  windowDays: number;
}

interface StuckCardProps extends SourceListCardProps {
  /**
   * PR 64 — the total action-items count rendered on the nav badge.
   * Used to explain the "1 blocking · 242 total" delta in the
   * subtitle so the two numbers don't read as a contradiction.
   */
  totalActionItems: number;
}

function StuckCard({ sources, windowDays, totalActionItems }: StuckCardProps) {
  // PR 64 — reconcile "What's stuck" (currently blocking enforcement)
  // with the nav action-items count (everything actionable, including
  // advisory items, drift alerts, watchlist items, etc.). The reviewer
  // flagged that "242 in nav" vs "1 here" reads as a credibility
  // problem on the same page. The subtitle now spells out the delta:
  // shown vs total + a link to the full list. Generic counts — no
  // hardcoded numbers.
  const blockingCount = sources.length;
  const advisoryCount = Math.max(0, totalActionItems - blockingCount);
  const subtitle =
    advisoryCount > 0
      ? `${blockingCount} currently blocking enforcement · ${advisoryCount} advisory in Action items. Trailing ${windowDays}d.`
      : `Top sources blocking enforcement progress (Rollout). Trailing ${windowDays}d.`;
  return (
    <ModeCard
      title="What's stuck"
      subtitle={subtitle}
      icon={Wind}
      accent="amber"
      emptyText="Nothing's blocking progress right now. When the matcher surfaces sources that need triage, they'll show up here."
      sources={sources}
      atRiskFraming="blocked at next step"
    />
  );
}

function BleedingCard({ sources, windowDays }: SourceListCardProps) {
  return (
    <ModeCard
      title="Currently bleeding"
      subtitle={`Legitimate mail being rejected today (Operate). Trailing ${windowDays}d.`}
      icon={ShieldAlert}
      accent="rose"
      emptyText="No bleeding right now — every Approved source is aligned."
      sources={sources}
      atRiskFraming="rejected today"
    />
  );
}

interface ModeCardProps {
  title: string;
  subtitle: string;
  icon: typeof Wind;
  accent: "amber" | "rose";
  emptyText: string;
  sources: DashboardSourceRow[];
  atRiskFraming: string;
}

function ModeCard({
  title,
  subtitle,
  icon: Icon,
  accent,
  emptyText,
  sources,
  atRiskFraming,
}: ModeCardProps) {
  const colors =
    accent === "amber"
      ? {
          border: "border-amber-200",
          bg: "bg-amber-50",
          headerBg: "bg-amber-100",
          iconColor: "text-amber-600",
          title: "text-amber-900",
        }
      : {
          border: "border-rose-200",
          bg: "bg-rose-50",
          headerBg: "bg-rose-100",
          iconColor: "text-rose-600",
          title: "text-rose-900",
        };

  return (
    <section className={`border rounded-xl overflow-hidden ${colors.border} ${colors.bg}`}>
      <header className={`flex items-center gap-3 px-4 py-3 ${colors.headerBg}`}>
        <Icon className={`w-5 h-5 ${colors.iconColor}`} />
        <div className="flex-1">
          <h2 className={`text-base font-semibold ${colors.title}`}>{title}</h2>
          <p className="text-xs text-slate-700">{subtitle}</p>
        </div>
      </header>

      {sources.length === 0 ? (
        <div className="px-4 py-5 text-sm text-slate-600 bg-white">
          {emptyText}
        </div>
      ) : (
        <ul className="bg-white divide-y divide-slate-100">
          {sources.map((s) => (
            <SourceListItem
              key={s.id}
              source={s}
              atRiskFraming={atRiskFraming}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function SourceListItem({
  source,
  atRiskFraming,
}: {
  source: DashboardSourceRow;
  atRiskFraming: string;
}) {
  const label =
    source.knownProviderName ??
    source.authIdentity ??
    source.cidrBucket ??
    source.asn ??
    source.id;

  return (
    <li className="px-4 py-3 hover:bg-slate-50 transition-colors">
      <Link to={`/sources/${source.id}`} className="flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2">
            <span className="font-medium text-slate-900 truncate">{label}</span>
            <span className="text-xs text-slate-500">{source.domainName}</span>
          </div>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-slate-600">
            <ClassificationDot classification={source.classification} />
            {source.health && <HealthDot health={source.health} />}
            <span>
              <span className="font-medium text-slate-900">
                {source.messagesInWindow.toLocaleString()}
              </span>{" "}
              msgs
            </span>
            {source.messagesFailingInWindow > 0 && (
              <span className="text-rose-700">
                <span className="font-medium">
                  {source.messagesFailingInWindow.toLocaleString()}
                </span>{" "}
                {atRiskFraming}
              </span>
            )}
          </div>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
      </Link>
    </li>
  );
}

function ClassificationDot({
  classification,
}: {
  classification: DashboardSourceRow["classification"];
}) {
  const color =
    classification === "Approved"
      ? "bg-emerald-500"
      : classification === "Forwarder"
        ? "bg-sky-400"
        : classification === "Not-Mine"
          ? "bg-rose-500"
          : "bg-amber-400";
  return (
    <span className="inline-flex items-center gap-1">
      <span className={`w-1.5 h-1.5 rounded-full ${color}`} />
      {classification}
    </span>
  );
}

function HealthDot({ health }: { health: NonNullable<DashboardSourceRow["health"]> }) {
  const color =
    health === "Aligned"
      ? "text-emerald-700"
      : health === "Misconfigured"
        ? "text-rose-700"
        : "text-slate-500";
  return <span className={color}>{health}</span>;
}

// ---------------------------------------------------------------------------
// ActionItemsCard — wired to GET /api/action-items unified endpoint (PR 14)
// ---------------------------------------------------------------------------

